interface BaseMessage {
  id: string;
  content: string;
  sender: "user" | "system";
  timestamp: string;
  flagged?: boolean;
}

interface TextMessage extends BaseMessage {
  type: "text";
}

interface CsvMessage extends BaseMessage {
  type: "csv";
}

interface TableMessage extends BaseMessage {
  type: "table";
  tableHeaders: string[];
  tableRows: string[][];
}

interface FileMessage extends BaseMessage {
  type: "file";
  fileName: string;
  fileSize: string;
  fileType: "csv" | "pdf";
}

export interface SummaryMetric {
  label: string;
  value: string;
  change: string;
  changeType: "up" | "down" | "neutral";
}

interface SummaryMessage extends BaseMessage {
  type: "summary";
  summaryMetrics: SummaryMetric[];
}

interface ListMessage extends BaseMessage {
  type: "list";
  listItems: string[];
}

interface ErrorMessage extends BaseMessage {
  type: "error";
  errorLevel: "error" | "warning";
  retryQuery?: string;
  retryable?: boolean;
}

export type ChatMessage =
  | TextMessage
  | CsvMessage
  | TableMessage
  | FileMessage
  | SummaryMessage
  | ListMessage
  | ErrorMessage;

export type MessageType = ChatMessage["type"];

export function formatTime(date?: Date): string {
  return (date || new Date()).toISOString();
}

// --- Raw CSV strings ---

const mockCSVUsers = `name,email,role,cohort,status
Sarah Chen,sarah.chen@example.com,mentor,Spring 2026,active
Alex Rodriguez,alex.r@example.com,mentee,Spring 2026,active
Jennifer Kim,jennifer.k@example.com,mentor,Spring 2026,active
Michael Brown,michael.b@example.com,mentee,Spring 2026,inactive
David Park,david.p@example.com,mentor,Winter 2025,active
Emily Johnson,emily.j@example.com,mentee,Winter 2025,active
Lisa Wang,lisa.w@example.com,mentor,Spring 2026,active
James Taylor,james.t@example.com,mentee,Spring 2026,active
Maria Garcia,maria.g@example.com,mentor,Winter 2025,active
Robert Lee,robert.l@example.com,mentee,Winter 2025,inactive
Amanda Foster,amanda.f@example.com,mentor,Spring 2026,active
Chris Martinez,chris.m@example.com,mentee,Spring 2026,active`;

const mockCSVMatches = `match_id,mentor,mentee,cohort,created_at,status
M001,Sarah Chen,Alex Rodriguez,Spring 2026,2026-01-15,active
M002,Jennifer Kim,Michael Brown,Spring 2026,2026-01-16,paused
M003,David Park,Emily Johnson,Winter 2025,2025-10-01,completed
M004,Sarah Chen,Emily Johnson,Winter 2025,2025-10-02,completed`;

const mockCSVCohorts = `cohort_id,name,type,start_date,end_date,members,status
C001,Spring 2026,standard,2026-01-10,2026-06-30,24,active
C002,Winter 2025,double_opt_in,2025-09-15,2025-12-20,18,completed
C003,Fall 2025,standard,2025-08-01,2025-11-30,32,completed
C004,Summer 2026,standard,2026-06-01,2026-08-31,0,draft
C005,Spring 2025,standard,2025-01-15,2025-06-15,28,completed
C006,Fall 2024,double_opt_in,2024-08-10,2024-12-01,15,completed
C007,Summer 2025,standard,2025-06-01,2025-08-30,20,completed
C008,Winter 2024,standard,2024-09-20,2024-12-18,12,completed
C009,Fall 2026,double_opt_in,2026-08-01,2026-11-30,0,draft
C010,Spring 2024,standard,2024-01-10,2024-05-30,22,completed`;

const mockCSVEmails = `email_id,type,recipient,cohort,sent_at,status,opened
E001,connection,alex.r@example.com,Spring 2026,2026-01-15 10:00,delivered,true
E002,connection,michael.b@example.com,Spring 2026,2026-01-15 10:00,bounced,false
E003,feedback,emily.j@example.com,Winter 2025,2025-11-01 09:00,delivered,true
E004,welcomePackage,sarah.chen@example.com,Spring 2026,2026-01-10 08:00,delivered,true
E005,checkIn,david.p@example.com,Winter 2025,2025-10-15 09:00,delivered,false
E006,introConsent,jennifer.k@example.com,Spring 2026,2026-01-12 11:00,delivered,true`;

const mockCSVPayments = `payment_id,user,plan,amount,currency,date,status
P001,Acme Corp,enterprise,$499.00,USD,2026-01-01,active
P002,TechStart Inc,pro,$199.00,USD,2026-01-05,active
P003,EduMentor,basic,$49.00,USD,2025-12-01,canceled
P004,GlobalConnect,enterprise,$499.00,USD,2025-11-15,active`;

const mockCSVEngagement = `user,role,check_ins_completed,feedback_submitted,meetings_held,last_active
Sarah Chen,mentor,8,3,12,2026-03-17
Alex Rodriguez,mentee,6,2,10,2026-03-18
Jennifer Kim,mentor,5,1,7,2026-03-15
Michael Brown,mentee,2,0,3,2026-02-28
David Park,mentor,10,4,15,2026-03-16
Emily Johnson,mentee,9,3,14,2026-03-18`;

// --- Helpers ---

// TODO: uses naive split(",") — replace with proper CSV parser when backend provides real data
function csvToTable(csv: string): { headers: string[]; rows: string[][] } {
  const lines = csv.trim().split("\n");
  const headers = lines[0].split(",");
  const rows = lines.slice(1).map((line) => line.split(","));
  return { headers, rows };
}

function getFileSize(content: string): string {
  const bytes = new Blob([content]).size;
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

function getFileName(query: string): string {
  const queryLower = query.toLowerCase();
  const matchedKey = Object.keys(dataResponses).find((key) =>
    queryLower.includes(key)
  );
  const label = matchedKey || "query";
  return `latte-${label}-export.csv`;
}

// --- Response mapping ---

interface DataResponseConfig {
  csv: string;
  summaryLabel: string;
  summaryValue: string;
  summaryChange: string;
  summaryChangeType: "up" | "down" | "neutral";
  listItems: string[];
}

const dataResponses: Record<string, DataResponseConfig> = {
  user: {
    csv: mockCSVUsers,
    summaryLabel: "Total Users",
    summaryValue: "12",
    summaryChange: "+4 this month",
    summaryChangeType: "up",
    listItems: [
      "Sarah Chen - Mentor (active)",
      "Alex Rodriguez - Mentee (active)",
      "Jennifer Kim - Mentor (active)",
      "Michael Brown - Mentee (inactive)",
      "David Park - Mentor (active)",
      "Emily Johnson - Mentee (active)",
      "Lisa Wang - Mentor (active)",
      "James Taylor - Mentee (active)",
      "Maria Garcia - Mentor (active)",
      "Robert Lee - Mentee (inactive)",
      "Amanda Foster - Mentor (active)",
      "Chris Martinez - Mentee (active)",
    ],
  },
  mentor: {
    csv: mockCSVUsers,
    summaryLabel: "Total Mentors",
    summaryValue: "3",
    summaryChange: "+1 this month",
    summaryChangeType: "up",
    listItems: ["Sarah Chen - Spring 2026", "Jennifer Kim - Spring 2026", "David Park - Winter 2025"],
  },
  mentee: {
    csv: mockCSVUsers,
    summaryLabel: "Total Mentees",
    summaryValue: "3",
    summaryChange: "No change",
    summaryChangeType: "neutral",
    listItems: ["Alex Rodriguez - Spring 2026", "Michael Brown - Spring 2026", "Emily Johnson - Winter 2025"],
  },
  match: {
    csv: mockCSVMatches,
    summaryLabel: "Total Matches",
    summaryValue: "4",
    summaryChange: "+2 this quarter",
    summaryChangeType: "up",
    listItems: [
      "Sarah Chen & Alex Rodriguez (active)",
      "Jennifer Kim & Michael Brown (paused)",
      "David Park & Emily Johnson (completed)",
      "Sarah Chen & Emily Johnson (completed)",
    ],
  },
  cohort: {
    csv: mockCSVCohorts,
    summaryLabel: "Active Cohorts",
    summaryValue: "1",
    summaryChange: "Spring 2026",
    summaryChangeType: "neutral",
    listItems: [
      "Spring 2026 - Standard (active, 24 members)",
      "Winter 2025 - Double Opt-In (completed, 18 members)",
      "Fall 2025 - Standard (completed, 32 members)",
      "Summer 2026 - Standard (draft, 0 members)",
      "Spring 2025 - Standard (completed, 28 members)",
      "Fall 2024 - Double Opt-In (completed, 15 members)",
      "Summer 2025 - Standard (completed, 20 members)",
      "Winter 2024 - Standard (completed, 12 members)",
      "Fall 2026 - Double Opt-In (draft, 0 members)",
      "Spring 2024 - Standard (completed, 22 members)",
    ],
  },
  email: {
    csv: mockCSVEmails,
    summaryLabel: "Emails Sent",
    summaryValue: "6",
    summaryChange: "1 bounced",
    summaryChangeType: "down",
    listItems: [
      "Connection emails: 2 sent",
      "Feedback emails: 1 sent",
      "Welcome Package: 1 sent",
      "Check-In: 1 sent",
      "Intro Consent: 1 sent",
    ],
  },
  payment: {
    csv: mockCSVPayments,
    summaryLabel: "Active Subscriptions",
    summaryValue: "3",
    summaryChange: "$1,197/mo revenue",
    summaryChangeType: "up",
    listItems: [
      "Acme Corp - Enterprise ($499/mo)",
      "TechStart Inc - Pro ($199/mo)",
      "EduMentor - Basic (canceled)",
      "GlobalConnect - Enterprise ($499/mo)",
    ],
  },
  subscription: {
    csv: mockCSVPayments,
    summaryLabel: "Active Subscriptions",
    summaryValue: "3",
    summaryChange: "$1,197/mo revenue",
    summaryChangeType: "up",
    listItems: [
      "Acme Corp - Enterprise ($499/mo)",
      "TechStart Inc - Pro ($199/mo)",
      "GlobalConnect - Enterprise ($499/mo)",
    ],
  },
  engagement: {
    csv: mockCSVEngagement,
    summaryLabel: "Avg. Check-ins",
    summaryValue: "6.7",
    summaryChange: "+12% vs last month",
    summaryChangeType: "up",
    listItems: [
      "Total check-ins completed: 40",
      "Total feedback submitted: 13",
      "Total meetings held: 61",
      "Most active: David Park (15 meetings)",
      "Least active: Michael Brown (3 meetings)",
    ],
  },
  "check-in": {
    csv: mockCSVEngagement,
    summaryLabel: "Check-ins Completed",
    summaryValue: "40",
    summaryChange: "+8 this week",
    summaryChangeType: "up",
    listItems: [
      "David Park: 10 check-ins",
      "Emily Johnson: 9 check-ins",
      "Sarah Chen: 8 check-ins",
      "Alex Rodriguez: 6 check-ins",
      "Jennifer Kim: 5 check-ins",
      "Michael Brown: 2 check-ins",
    ],
  },
  feedback: {
    csv: mockCSVEngagement,
    summaryLabel: "Feedback Submitted",
    summaryValue: "13",
    summaryChange: "+3 this week",
    summaryChangeType: "up",
    listItems: [
      "David Park: 4 submissions",
      "Sarah Chen: 3 submissions",
      "Emily Johnson: 3 submissions",
      "Alex Rodriguez: 2 submissions",
      "Jennifer Kim: 1 submission",
      "Michael Brown: 0 submissions",
    ],
  },
};

// --- Suggested prompts ---

// TODO: Implement PDF export support — CSV export is supported via the "csv" AI response type.
// PDF export should generate formatted reports from table/summary data.
export const suggestedPrompts = [
  "Show all users",
  "List active cohorts",
  "NPS score",
  "Email delivery rate",
  "Match statistics",
];

// --- Mock conversation (empty = show empty state) ---

export const mockConversation: ChatMessage[] = [];

// --- Response generators ---

function detectResponseType(query: string): MessageType {
  const lower = query.toLowerCase();
  if (
    lower.includes("download") ||
    lower.includes("export") ||
    lower.includes("csv file") ||
    lower.includes(".csv") ||
    lower.includes("formatted file") ||
    lower.includes("pdf") ||
    lower.includes("report")
  ) {
    return "file";
  }
  if (
    lower.includes("how many") ||
    lower.includes("count") ||
    lower.includes("total") ||
    lower.includes("summary")
  ) {
    return "summary";
  }
  if (lower.includes("list") || lower.includes("show me all") || lower.includes("names")) {
    return "list";
  }
  if (lower.includes("table") || lower.includes("show")) {
    return "table";
  }
  return "csv";
}

export const getMockResponse = (query: string): ChatMessage => {
  const queryLower = query.toLowerCase();

  // Trigger warning demo: type "warning" to see the warning component
  if (queryLower.includes("warning")) {
    return {
      id: crypto.randomUUID(),
      content: "No data to export yet. Run a query first, then ask me to download it.",
      sender: "system",
      timestamp: formatTime(),
      type: "error",
      errorLevel: "warning",
    };
  }

  const matchedKey = Object.keys(dataResponses).find((key) =>
    queryLower.includes(key)
  );

  if (!matchedKey) {
    return {
      id: crypto.randomUUID(),
      content: `No results found for "${query}". Try asking about: users, matches, cohorts, emails, payments, or engagement.`,
      sender: "system",
      timestamp: formatTime(),
      type: "error",
      errorLevel: "warning",
    };
  }

  const config = dataResponses[matchedKey];
  const responseType = detectResponseType(query);

  switch (responseType) {
    case "file":
      return getMockFileResponse(query, config.csv);

    case "summary":
      return {
        id: crypto.randomUUID(),
        content: config.summaryLabel,
        sender: "system",
        timestamp: formatTime(),
        type: "summary",
        summaryMetrics: [{
          label: config.summaryLabel,
          value: config.summaryValue,
          change: config.summaryChange,
          changeType: config.summaryChangeType,
        }],
      };

    case "list":
      return {
        id: crypto.randomUUID(),
        content: config.listItems.join("\n"),
        sender: "system",
        timestamp: formatTime(),
        type: "list",
        listItems: config.listItems,
      };

    case "table": {
      const { headers, rows } = csvToTable(config.csv);
      return {
        id: crypto.randomUUID(),
        content: config.csv,
        sender: "system",
        timestamp: formatTime(),
        type: "table",
        tableHeaders: headers,
        tableRows: rows,
      };
    }

    default:
      return {
        id: crypto.randomUUID(),
        content: config.csv,
        sender: "system",
        timestamp: formatTime(),
        type: "csv",
      };
  }
};

function detectFileType(query: string): "csv" | "pdf" {
  const lower = query.toLowerCase();
  if (lower.includes("pdf") || lower.includes("report")) return "pdf";
  return "csv";
}

const getMockFileResponse = (query: string, csvContent: string): ChatMessage => {
  const fileType = detectFileType(query);
  const fileName = fileType === "pdf"
    ? getFileName(query).replace(".csv", ".pdf")
    : getFileName(query);

  return {
    id: crypto.randomUUID(),
    content: csvContent,
    sender: "system",
    timestamp: formatTime(),
    type: "file",
    fileName,
    fileSize: getFileSize(csvContent),
    fileType,
  };
};
